import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast, ToastContainer } from 'react-toastify';
import AdminSidebar from 'admin/sidebar/AdminSidebar';
import './SuperAdminPackages.css';

const API_BASE = process.env.REACT_APP_API_URL || 'https://backend.washta.com';
const LOCATIONS_URL = `${API_BASE}/api/package-sets/admin/locations`;
const SETS_URL = `${API_BASE}/api/package-sets/admin`;

const authHeader = () => ({
  Authorization: `Bearer ${localStorage.getItem('accessTokenAdmin')}`,
});

const PackageLocations = () => {
  const [rows, setRows] = useState([]);
  const [sets, setSets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [assignRow, setAssignRow] = useState(null);
  const [pickedSetId, setPickedSetId] = useState('');

  const fetchAll = async () => {
    try {
      setLoading(true);
      const [locRes, setsRes] = await Promise.all([
        axios.get(LOCATIONS_URL, { headers: authHeader() }),
        axios.get(SETS_URL, { headers: authHeader() }),
      ]);
      setRows(Array.isArray(locRes?.data?.data) ? locRes.data.data : []);
      setSets(Array.isArray(setsRes?.data?.data) ? setsRes.data.data : []);
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Failed to load package locations');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { window.scrollTo(0, 0); fetchAll(); }, []);

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      r.displayName?.toLowerCase().includes(q) ||
      r.city?.toLowerCase().includes(q) ||
      r.shopName?.toLowerCase().includes(q) ||
      r.sellerName?.toLowerCase().includes(q)
    );
  }, [rows, search]);

  const stats = useMemo(() => ({
    total: rows.length,
    activePkgs: rows.reduce((s, r) => s + (r.activePackages || 0), 0),
    withSingleWash: rows.filter((r) => r.singleWashEnabled).length,
    noSet: rows.filter((r) => !r.setId).length,
  }), [rows]);

  const handleToggleFlag = async (row, flag) => {
    // Optimistic update — flip locally first, no full reload
    setRows(prev => prev.map(r =>
      r.locationId === row.locationId ? { ...r, [flag]: !r[flag] } : r
    ));
    try {
      await axios.patch(`${LOCATIONS_URL}/${row.locationId}/toggle-flag`, { shopId: row.shopId, flag }, { headers: authHeader() });
    } catch (err) {
      // Revert on failure
      setRows(prev => prev.map(r =>
        r.locationId === row.locationId ? { ...r, [flag]: row[flag] } : r
      ));
      toast.error(err?.response?.data?.error || 'Failed to toggle flag');
    }
  };

  const openAssign = (row) => {
    setAssignRow(row);
    setPickedSetId(row.setId || '');
  };

  const handleAssign = async () => {
    if (!pickedSetId) return toast.error('Pick a set first');
    try {
      await axios.post(`${LOCATIONS_URL}/${assignRow.locationId}/assign`, {
        shopId: assignRow.shopId,
        sellerId: assignRow.sellerId,
        setId: pickedSetId,
        locationDisplayName: assignRow.displayName,
      }, { headers: authHeader() });
      toast.success('Set assigned');
      setAssignRow(null);
      fetchAll();
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Failed to assign set');
    }
  };

  const handleUnassign = async (row) => {
    if (!window.confirm(`Remove the package set from ${row.displayName}?`)) return;
    try {
      await axios.delete(`${LOCATIONS_URL}/${row.locationId}/unassign`, {
        headers: authHeader(), data: { sellerId: row.sellerId },
      });
      toast.success('Set unassigned');
      fetchAll();
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Failed to unassign set');
    }
  };

  const setsForRow = (row) => sets.filter((s) => String(s.sellerId) === String(row.sellerId) && s.isActive);

  return (
    <div className="mainDiv-all-sideBar">
      <div className="div-sidebar"><AdminSidebar /></div>
      <div className="div-Dashbaord" style={{background:'white',padding:0}}>
        <div className="width-settimg addtionwidth-setting sap-page">

          <div className="sap-title">Package Locations</div>
          <div className="sap-sub">
  Locations where monthly packages are sold. These are managed separately from the single-wash location list, but they use the same underlying location record. Channel flags (single wash / packages) can be toggled independently from here.
</div>

          <div className="sap-stats">
            <div className="sap-stat"><div className="sap-stat-val">{stats.total}</div><div className="sap-stat-label">Package locations</div></div>
            <div className="sap-stat"><div className="sap-stat-val green">{stats.activePkgs}</div><div className="sap-stat-label">Active packages</div></div>
            <div className="sap-stat"><div className="sap-stat-val">{stats.withSingleWash}</div><div className="sap-stat-label">Also sell single wash</div></div>
            <div className="sap-stat"><div className="sap-stat-val amber">{stats.noSet}</div><div className="sap-stat-label">No set assigned</div></div>
          </div>

          <div className="sap-fbar">
            <div className="sap-fbar-row">
              <input className="sap-finput" style={{ width: 240 }} placeholder="🔍 Search location, city or provider…"
                value={search} onChange={(e) => setSearch(e.target.value)} />
            </div>
          </div>

          {loading ? (
            <div className="sap-muted">Loading…</div>
          ) : (
            <table className="sap-tbl">
              <thead>
                <tr>
                  <th>Location</th><th>Type</th><th>Provider</th><th>Package set</th>
                  <th>Packages</th>
                  <th>Active pkgs</th><th>Due today</th>
                </tr>
              </thead>
              <tbody>
                {filteredRows.map((row) => (
                  <tr key={row.locationId} className={!row.setId ? 'warn-row' : ''}>
                    <td><b>{row.displayName}</b><div className="sap-muted">{row.city}</div></td>
                    <td><span className="sap-pill acc">{row.locationType}</span></td>
                    <td>{row.sellerName || row.shopName}</td>
                    <td>{row.setName ? <span className="sap-mono">{row.setName}</span> : <span className="sap-pill err">⚠ Not assigned</span>}</td>
                    <td>
                      <button className={`sap-toggle ${row.packagesEnabled ? 'on' : ''}`} onClick={() => handleToggleFlag(row, 'packagesEnabled')}>
                        <span className="sap-toggle-knob" />
                      </button>
                    </td>
                    <td><b>{row.activePackages}</b></td>
                    <td>{row.dueToday}</td>
                    {/* <td>
                      {row.setId ? (
                        <div style={{ display: 'flex', gap: 8 }}>
                          <span className="sap-action-link" onClick={() => openAssign(row)}>Change</span>
                          <span className="sap-action-link" style={{ color: '#991b1b' }} onClick={() => handleUnassign(row)}>Remove</span>
                        </div>
                      ) : (
                        <span className="sap-action-link" onClick={() => openAssign(row)}>Assign set</span>
                      )}
                    </td> */}
                  </tr>

                ))}
                {filteredRows.length === 0 && (
                  <tr><td colSpan={7} className="sap-muted" style={{ textAlign: 'center', padding: 20 }}>There is no location.</td></tr>
                )}
              </tbody>
            </table>
          )}

          <div className="sap-info" style={{ marginTop: 16 }}>
            The app banner only shows when a location sells single wash AND packages AND has an active set with
            ≥1 live tier. This is derived automatically and is never picked manually.
          </div>
        </div>
      </div>

      {assignRow && (
        <div className="sap-modal-overlay" onClick={() => setAssignRow(null)}>
          <div className="sap-modal" style={{ maxWidth: 420 }} onClick={(e) => e.stopPropagation()}>
            <div className="sap-modal-head">
              <h3>Assign set — {assignRow.displayName}</h3>
              <button className="sap-modal-close" onClick={() => setAssignRow(null)}>×</button>
            </div>
            <label className="sap-form-label">Package set ({assignRow.shopName})</label>
            <select className="sap-form-input" value={pickedSetId} onChange={(e) => setPickedSetId(e.target.value)}>
              <option value="">Select a set…</option>
              {setsForRow(assignRow).map((s) => (
                <option key={s._id} value={s._id}>{s.name}</option>
              ))}
            </select>
            {setsForRow(assignRow).length === 0 && (
              <div className="sap-warn" style={{ marginTop: 12 }}>This provider has no active package set.</div>
            )}
            <div className="sap-modal-actions">
              <button type="button" className="sap-btn ghost" onClick={() => setAssignRow(null)}>Cancel</button>
              <button type="button" className="sap-btn primary" onClick={handleAssign}>Assign</button>
            </div>
          </div>
        </div>
      )}

      <ToastContainer position="bottom-right" />
    </div>
  );
};

export default PackageLocations;
